import { useState, useEffect, useRef, useMemo } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from '@/contexts/BusinessContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavOrder } from '@/hooks/useNavOrder';
import {
  Hop as Home,
  DollarSign,
  Receipt,
  ChartBar as BarChart3,
  Wallet,
  Settings,
  LogOut,
  ArrowLeftRight,
  Menu,
  X,
  Users,
  Database,
  ArrowRightLeft,
  GripVertical,
  RotateCcw,
} from 'lucide-react';
import TripCompassLogo from '@/assets/images/Trip_Compass_LLC.png';
import PageTransition from '@/components/PageTransition';

const SIDEBAR_WIDTH = 224;

const NAV_ITEMS_MAP: Record<string, { icon: typeof Home; label: string }> = {
  '/': { icon: Home, label: 'Dashboard' },
  '/payments': { icon: DollarSign, label: 'Transactions' },
  '/customers': { icon: Users, label: 'Customers' },
  '/database': { icon: Database, label: 'Database' },
  '/money-flow': { icon: ArrowRightLeft, label: 'Money Flow' },
  '/expenses': { icon: Receipt, label: 'Expenses' },
  '/reports': { icon: BarChart3, label: 'Reports' },
  '/balances': { icon: Wallet, label: 'Wise/Mercury' },
  '/settings': { icon: Settings, label: 'Settings' },
};

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : false
  );

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    setIsDesktop(mq.matches);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isDesktop;
}

export default function DashboardLayout() {
  const { selectedBusiness } = useBusiness();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [reorderMode, setReorderMode] = useState(false);
  const isDesktop = useIsDesktop();
  const { order, reorder, resetOrder } = useNavOrder();

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const sortedNavItems = useMemo(
    () => order.filter(route => NAV_ITEMS_MAP[route]).map(route => ({ to: route, ...NAV_ITEMS_MAP[route] })),
    [order],
  );

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  const handleDragStart = (index: number) => {
    dragItem.current = index;
    setDragIndex(index);
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
    setOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      reorder(dragItem.current, dragOverItem.current);
    }
    dragItem.current = null;
    dragOverItem.current = null;
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleTouchStart = (index: number) => {
    dragItem.current = index;
    setDragIndex(index);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    const elements = document.elementsFromPoint(touch.clientX, touch.clientY);
    const navItem = elements.find(el => el.hasAttribute('data-nav-index'));
    if (navItem) {
      const idx = Number(navItem.getAttribute('data-nav-index'));
      dragOverItem.current = idx;
      setOverIndex(idx);
    }
  };

  const handleTouchEnd = () => {
    handleDragEnd();
  };

  const sidebarVisible = isDesktop || sidebarOpen;

  return (
    <div className="h-screen overflow-hidden relative">
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 blur-md"
        style={{ backgroundImage: "url('/assets/images/Background-6.png')" }}
      />
      <div className="absolute inset-0 bg-black/25" />

      <div className="relative z-10 h-full p-3">
      {sidebarOpen && !isDesktop && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <aside
        className="w-56 glass-panel rounded-2xl flex flex-col fixed top-3 bottom-3 left-3 z-40 transition-transform duration-300 ease-in-out"
        style={{ transform: sidebarVisible ? 'translateX(0)' : 'translateX(calc(-100% - 24px))' }}
      >
        <div className="px-4 py-5 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            {selectedBusiness?.logo_url ? (
              <img
                src={selectedBusiness.logo_url}
                alt={selectedBusiness.name}
                className="w-10 h-10 rounded-lg object-cover ring-2 ring-white/10"
              />
            ) : (
              <img
                src={TripCompassLogo}
                alt={selectedBusiness?.name || 'Business'}
                className="w-10 h-10 rounded-lg object-cover ring-2 ring-white/10"
              />
            )}
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-white truncate text-sm leading-tight">
                {selectedBusiness?.name || 'Business'}
              </h2>
              <p className="text-[11px] text-emerald-400/60 truncate">X Manage</p>
            </div>
            {!isDesktop && (
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1.5 text-white/50 hover:text-white rounded-lg glass-hover"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="px-3 pt-2 pb-1">
          <button
            onClick={() => navigate('/businesses')}
            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-xs text-white/50 hover:text-white glass-nav-item"
          >
            <ArrowLeftRight size={13} />
            <span>Switch Business</span>
          </button>
        </div>

        <nav className="flex-1 px-3 py-2 overflow-y-auto">
          {reorderMode && (
            <div className="flex items-center justify-between px-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">Drag to reorder</span>
              <button
                onClick={() => { resetOrder(); }}
                className="text-[10px] text-white/40 hover:text-slate-300 transition-colors"
                title="Reset to default"
              >
                <RotateCcw size={12} />
              </button>
            </div>
          )}
          <ul className="space-y-0.5">
            {sortedNavItems.map((item, index) => (
              <li
                key={item.to}
                data-nav-index={index}
                draggable={reorderMode}
                onDragStart={reorderMode ? () => handleDragStart(index) : undefined}
                onDragEnter={reorderMode ? () => handleDragEnter(index) : undefined}
                onDragOver={reorderMode ? (e) => e.preventDefault() : undefined}
                onDragEnd={reorderMode ? handleDragEnd : undefined}
                onTouchStart={reorderMode ? () => handleTouchStart(index) : undefined}
                onTouchMove={reorderMode ? handleTouchMove : undefined}
                onTouchEnd={reorderMode ? handleTouchEnd : undefined}
                className={`transition-all duration-150 rounded-lg ${
                  reorderMode && dragIndex === index ? 'opacity-40 scale-95' : ''
                } ${
                  reorderMode && overIndex === index && dragIndex !== index
                    ? 'ring-1 ring-emerald-400/50 bg-emerald-500/5'
                    : ''
                }`}
              >
                {reorderMode ? (
                  <div className="flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-white/50 cursor-grab active:cursor-grabbing select-none">
                    <GripVertical size={14} className="text-white/50 shrink-0" />
                    <item.icon size={16} strokeWidth={1.8} />
                    <span>{item.label}</span>
                  </div>
                ) : (
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium ${
                        isActive
                          ? 'glass-nav-active text-emerald-400'
                          : 'glass-nav-item text-white/50 hover:text-white'
                      }`
                    }
                  >
                    <item.icon size={16} strokeWidth={1.8} />
                    <span>{item.label}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="px-3 py-2 border-t border-white/[0.06] mt-auto">
          <button
            onClick={() => setReorderMode(!reorderMode)}
            className={`flex items-center gap-2.5 px-3 py-2 w-full rounded-lg text-[13px] font-medium ${
              reorderMode
                ? 'glass-nav-active text-emerald-400'
                : 'glass-nav-item text-white/50 hover:text-white'
            }`}
          >
            <GripVertical size={16} strokeWidth={1.8} />
            <span>{reorderMode ? 'Done' : 'Reorder Pages'}</span>
          </button>
          <button
            onClick={handleSignOut}
            className="flex items-center gap-2.5 px-3 py-2 w-full rounded-lg text-[13px] font-medium text-white/50 hover:text-red-400 glass-nav-item"
          >
            <LogOut size={16} strokeWidth={1.8} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      <div
        className="h-full flex flex-col overflow-hidden"
        style={{ marginLeft: isDesktop ? `${SIDEBAR_WIDTH + 12}px` : 0 }}
      >
        {!isDesktop && (
          <div className="glass-panel rounded-2xl px-4 py-3 flex items-center gap-3 shrink-0 mb-3">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 -ml-1 text-white/70 rounded-xl glass-hover"
            >
              <Menu size={20} />
            </button>
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              {selectedBusiness?.logo_url ? (
                <img src={selectedBusiness.logo_url} alt="" className="w-7 h-7 rounded-lg object-cover" />
              ) : (
                <img src={TripCompassLogo} alt="" className="w-7 h-7 rounded-lg object-cover" />
              )}
              <span className="font-semibold text-white text-sm truncate">
                {selectedBusiness?.name || 'Business'}
              </span>
            </div>
          </div>
        )}

        <main className="flex-1 overflow-auto">
          <PageTransition>
            <Outlet />
          </PageTransition>
        </main>
      </div>
      </div>
    </div>
  );
}
